'use client'

import type { Transport } from 'viem'

import { getCdnUrl } from '@morpho-org/dapp-core'
import { CaipNetworksUtil } from '@reown/appkit-utils'
import { getWagmiAdapter, isChainId } from '@repo/web3'
import { type CreateConnectorFn, createStorage } from 'wagmi'
import { baseAccount, safe } from 'wagmi/connectors'

import { ChainConfig, clientEnvs } from '@/config'
import { getDeploymentHeaders } from '@/config/deployment'
import { originToRegExp, SAFE_APP_ORIGIN } from '@/config/embedders'
import { instrumentedHttp } from '@/lib/observability/client/trackers/rpc-usage'
import { logger } from '@/lib/observability/logger'

import { injectedWalletConnector } from '../utils/injectedWallet'
import { lazyTempoWallet } from '../utils/lazyTempoWallet'

// Strip AppKit's injected WalletConnect RPC so reads stay on our eRPC proxy only.
CaipNetworksUtil.extendWagmiTransports = (_caipNetwork, _projectId, transport) => transport

const isE2E = clientEnvs.NEXT_PUBLIC_FALLBACK_FLAGS_ENVIRONMENT === 'e2e'

const httpTransportConfig = {
  batch: {
    batchSize: 30,
    wait: 50
  }
}

// The Safe iframe blocks third-party cookies, so it needs localStorage. Everywhere
// else we return undefined and fall through to getWagmiAdapter's cookie-storage default.
const getStorage = (isSafeApp: boolean) => {
  if (!isSafeApp) return undefined
  try {
    return typeof localStorage !== 'undefined' && localStorage !== null
      ? createStorage({ storage: localStorage })
      : undefined
  } catch {
    return undefined
  }
}

// One instrumented eRPC transport per chain (getWagmiAdapter accepts any Transport).
const getTransports = (): Record<number, Transport> =>
  Object.fromEntries(
    ChainConfig.AVAILABLE_CHAIN_IDS.map(chainId => [
      chainId,
      instrumentedHttp(ChainConfig.getErpcProxyUrl(chainId), {
        ...httpTransportConfig,
        chainId,
        fetchOptions: { headers: getDeploymentHeaders() }
      })
    ])
  )

// AppKit adds its own baseAccount() unless one is registered. Its telemetry injects an
// inline script (blocked by CSP outside production), so register ours with it off.
const baseAccountConnector = () => baseAccount({ preference: { options: 'all', telemetry: false } })

// Safe bridge in the Safe iframe, injected wallet in E2E, else Tempo (default).
const getConnectors = (isSafeApp: boolean): CreateConnectorFn[] => {
  if (isSafeApp) {
    return [
      safe({
        allowedDomains: [originToRegExp(SAFE_APP_ORIGIN)],
        // Avoids throwing errors in tests when the main frame takes time to respond.
        unstable_getInfoTimeout: 500
      }),
      baseAccountConnector()
    ]
  }
  return isE2E
    ? [injectedWalletConnector(), baseAccountConnector()]
    : [lazyTempoWallet(), baseAccountConnector()]
}

/** dApp identity AppKit self-reports to wallets over WalletConnect. */
export const APP_METADATA = {
  name: 'Morpho',
  description: 'Open infrastructure for onchain loans',
  url: clientEnvs.NEXT_PUBLIC_DEPLOYMENT_URL,
  icons: [getCdnUrl('assets/logos/morpho.svg')]
}

// blue-sdk's ChainId is wider than @repo/web3's, and an id it doesn't know throws in
// getWagmiAdapter's eager getChain, which is fatal at provider mount. Dropping one costs
// nothing: `chainIds` only feeds an rpc-url map that goes unused because we pass `transports`.
const web3ChainIds = ChainConfig.AVAILABLE_CHAIN_IDS.flatMap(chainId =>
  isChainId(chainId) ? [chainId] : []
)

if (web3ChainIds.length !== ChainConfig.AVAILABLE_CHAIN_IDS.length) {
  logger.warn('Chain ids unknown to @repo/web3 excluded from the wagmi adapter', {
    extra: { chainIds: ChainConfig.AVAILABLE_CHAIN_IDS.filter(id => !isChainId(id)) }
  })
}

const createVaultsWagmiAdapter = (isSafeApp: boolean) =>
  getWagmiAdapter({
    chainIds: web3ChainIds,
    // The Vaults app's chain list (a subset of @repo/web3's registry, with the Vaults app's display-name overrides).
    // oxlint-disable-next-line typescript/consistent-type-assertions
    chains: ChainConfig.AVAILABLE_CHAINS as unknown as Parameters<
      typeof getWagmiAdapter
    >[0]['chains'],
    projectId: clientEnvs.NEXT_PUBLIC_WALLET_CONNECT_PROJECT_ID,
    transports: getTransports(),
    connectors: getConnectors(isSafeApp),
    batch: {
      multicall: {
        batchSize: 8192,
        wait: 50
      }
    },
    options: {
      ssr: true,
      storage: getStorage(isSafeApp)
    }
  })

const adapters = new Map<boolean, ReturnType<typeof createVaultsWagmiAdapter>>()

/**
 * One adapter per `isSafeApp` (resolved at mount from the iframe host), shared across every
 * render in the browser. `createAppKit` is a module singleton bound to the adapter handed to
 * it first, and React may throw a render away before commit (a Suspense retry during
 * hydration, StrictMode's double invocation). A fresh adapter per render then leaves AppKit
 * on a wagmi config that never mounts under `WagmiProvider`: under `ssr: true` that config
 * never rehydrates, so it drops every EIP-6963 announcement and the modal lists no wallet.
 *
 * Browser only: the server has no AppKit singleton, and `WagmiProvider` hydrates the config
 * with the request's `wagmi.store` cookie, so one module-level config there would leak
 * connection state across concurrent requests and mismatch the client on hydration.
 */
export const getVaultsWagmiAdapter = (isSafeApp: boolean) => {
  if (typeof window === 'undefined') return createVaultsWagmiAdapter(isSafeApp)
  let adapter = adapters.get(isSafeApp)
  if (!adapter) {
    adapter = createVaultsWagmiAdapter(isSafeApp)
    adapters.set(isSafeApp, adapter)
  }
  return adapter
}
